import { useState, useEffect } from 'react';
import { IndianRupee, Calendar, User, Trash2, CreditCard, Banknote, Smartphone, Building } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentsPage = () => {
  const { token } = useAuth();
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState({ total_payments: 0, payments_this_month: 0 });
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchPayments();
    fetchStats();
  }, [token]);

  const fetchPayments = async () => {
    try {
      const response = await axios.get(`${API}/payments?limit=100`, { headers });
      setPayments(response.data);
    } catch (error) {
      toast.error('Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/payments/stats`, { headers });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching payment stats:', error);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment record?')) return;
    try {
      await axios.delete(`${API}/payments/${paymentId}`, { headers });
      toast.success('Payment deleted successfully');
      fetchPayments();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete payment');
    }
  };

  const getPaymentModeIcon = (mode) => {
    switch (mode) {
      case 'cash': return <Banknote className="w-4 h-4" />;
      case 'upi': return <Smartphone className="w-4 h-4" />;
      case 'card': return <CreditCard className="w-4 h-4" />;
      case 'bank_transfer': return <Building className="w-4 h-4" />;
      default: return <IndianRupee className="w-4 h-4" />;
    }
  };

  const getPaymentModeLabel = (mode) => {
    switch (mode) {
      case 'cash': return 'Cash';
      case 'upi': return 'UPI';
      case 'card': return 'Card';
      case 'bank_transfer': return 'Bank Transfer';
      default: return mode;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-zinc-400">Loading payments...</div>
      </div>
    );
  }

  return (
    <div data-testid="payments-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-['Barlow_Condensed'] font-extrabold text-3xl lg:text-4xl uppercase tracking-tight text-white">
          Payments
        </h1>
        <p className="text-zinc-400 mt-1">All received payments</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="stat-card">
          <div className="flex items-start justify-between mb-4">
            <div className="w-12 h-12 bg-emerald-400/10 flex items-center justify-center">
              <IndianRupee className="w-6 h-6 text-emerald-400" />
            </div>
          </div>
          <p className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 mb-1">
            Total Collection
          </p>
          <p className="font-['Barlow_Condensed'] font-extrabold text-4xl text-emerald-400">
            ₹{stats.total_payments.toLocaleString()}
          </p>
        </div>
        <div className="stat-card">
          <div className="flex items-start justify-between mb-4">
            <div className="w-12 h-12 bg-[#FF3B30]/10 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-[#FF3B30]" />
            </div>
          </div>
          <p className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 mb-1">
            This Month
          </p>
          <p className="font-['Barlow_Condensed'] font-extrabold text-4xl text-[#FF3B30]">
            ₹{stats.payments_this_month.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Payments Table */}
      <div className="card-glass overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold">Member</th>
                <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold">Amount</th>
                <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold hidden md:table-cell">Date</th>
                <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold hidden sm:table-cell">Mode</th>
                <th className="text-right p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold">Action</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr
                  key={payment.id}
                  data-testid={`payment-row-${payment.id}`}
                  className="border-b border-zinc-800/50 table-row-hover"
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-500/20 flex items-center justify-center">
                        <span className="font-['Barlow_Condensed'] font-bold text-emerald-400">
                          {payment.member_name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-white">{payment.member_name}</p>
                        <p className="text-xs text-zinc-500">{payment.member_phone}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="font-['Barlow_Condensed'] font-bold text-xl text-emerald-400">
                      ₹{payment.amount.toLocaleString()}
                    </span>
                  </td>
                  <td className="p-4 hidden md:table-cell">
                    <div className="flex items-center gap-1 text-zinc-300">
                      <Calendar className="w-3 h-3" />
                      {payment.payment_date}
                    </div>
                  </td>
                  <td className="p-4 hidden sm:table-cell">
                    <div className="flex items-center gap-2 text-zinc-300">
                      {getPaymentModeIcon(payment.payment_mode)}
                      <span className="text-sm">{getPaymentModeLabel(payment.payment_mode)}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end">
                      <Button
                        data-testid={`delete-payment-${payment.id}`}
                        onClick={() => handleDeletePayment(payment.id)}
                        variant="ghost"
                        size="sm"
                        className="text-zinc-400 hover:text-red-400 p-2"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {payments.length === 0 && (
          <div className="text-center py-12">
            <p className="text-zinc-500">No payments recorded yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentsPage;
